import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Paper
} from '@mui/material';

interface AnalysisModuleProps {
    open: boolean;
    onClose: () => void;
    agentLoading: boolean;
    agentError: string | null;
    result: string | null;
}

const AnalysisModule: React.FC<AnalysisModuleProps> = ({
                                                           open,
                                                           onClose,
                                                           agentLoading,
                                                           agentError,
                                                           result
                                                       }) => {
    // Simple threat detection - just look for key indicators
    const getSimpleThreatStatus = (text: string | null) => {
        if (!text) return { level: 'unknown', color: 'info' as const };

        const lowerText = text.toLowerCase();

        if (lowerText.includes('malicious') && !lowerText.includes('benign')) {
            return { level: 'threat', color: 'error' as const };
        }

        if (lowerText.includes('benign') || lowerText.includes('clean') || lowerText.includes('safe')) {
            return { level: 'safe', color: 'success' as const };
        }

        return { level: 'analyzed', color: 'info' as const };
    };

    const threatStatus = getSimpleThreatStatus(result);

    const getStatusMessage = () => {
        switch (threatStatus.level) {
            case 'threat':
                return 'Potential threat detected';
            case 'safe':
                return 'File appears safe';
            default:
                return 'Analysis complete';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle>
                <Typography variant="h6">
                    Security Analysis Results
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {agentLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : agentError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {agentError}
                    </Alert>
                ) : result ? (
                    <Box>
                        {/* Simple status indicator */}
                        <Alert severity={threatStatus.color} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {getStatusMessage()}
                            </Typography>
                        </Alert>

                        {/* Raw analysis results */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Full Analysis Report:
                        </Typography>
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                maxHeight: 400,
                                overflow: 'auto'
                            }}
                        >
                            <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4,
                                    margin: 0
                                }}
                            >
                                {result}
                            </Typography>
                        </Paper>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ minWidth: 100 }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AnalysisModule;
